import { useTranslate } from 'hooks/useTranslate';
import { FC, useState } from 'react';
import { store } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';

import { Council, Document } from 'types';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addDocument } from 'modules/documents/redux/documentActions';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import { updateSocietyWithDocument } from 'modules/administratives/utils/updateSocietyWithDocument';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import { updateDocumentToDeleted } from 'utils/updateDocumentToDeleted';
import { extractFileNameAndExtension } from 'utils/extractFileNameAndExtension';

import AddCouncilDocumentModalBody from './AddCouncilDocumentModalBody';
import AddCouncilDocumentModalHeader from './AddCouncilDocumentModalHeader';
import AddCouncilDocumentModalFooter from './AddCouncilDocumentModalFooter';

type Props = {
  council: Council;
  privateByDefault?: boolean;
  replaceAnnouncementDocument?: boolean;
  fetchCouncilData: () => void;
};

const AddCouncilDocumentModal: FC<Props> = ({
  council,
  privateByDefault,
  replaceAnnouncementDocument,
  fetchCouncilData,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user: any = useSelector((state: any) => state.user);

  const PUBLIC_PRIVACY = 'PUBLIC';
  const PRIVATE_PRIVACY = 'PRIVATE';

  const [documentName, setDocumentName] = useState<string>('');
  const [documentExtension, setDocumentExtension] = useState<string>('');
  const [documentFile, setDocumentFile] = useState<File | undefined>(undefined);
  const [isPrivateDocument, setIsPrivateDocument] = useState<boolean>(
    !!privateByDefault
  );
  const [documentPrivacyType, setDocumentPrivacyType] = useState<string>(
    privateByDefault ? PRIVATE_PRIVACY : PUBLIC_PRIVACY
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChangeDocumentName = (event: any) => {
    setDocumentName(event.target.value);
  };

  const handleChangeDocumentFile = (file: File) => {
    const { name, extension } = extractFileNameAndExtension(file.name);

    setDocumentFile(file);
    setDocumentName(name);
    setDocumentExtension(extension);
  };

  const handleChangeIsPrivateDocument = () => {
    setIsPrivateDocument((prev) => !prev);
    setDocumentPrivacyType((prev) =>
      prev === PUBLIC_PRIVACY ? PRIVATE_PRIVACY : PUBLIC_PRIVACY
    );
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const createDocumentData = () => ({
    file: documentFile,
    date: new Date(),
    size: documentFile?.size || 0,
    name: `${documentName}.${documentExtension}`,
    author: user?._id,
    society: council?.society,
    fileType: fileTypes[documentFile?.type as keyof typeof fileTypes],
    category: documentTypes.DOCUMENTS,
    subcategory: documentPrivacyType,
    description: t('DocumentLinkedManually'),
  });

  const updateCouncilWithDocument = async (uploadedDocument: Document) => {
    const documentsKey =
      uploadedDocument.subcategory === PUBLIC_PRIVACY
        ? 'publicDocuments'
        : 'privateDocuments';

    const councilData = {
      _id: council?._id,
      [documentsKey]: [
        uploadedDocument._id,
        ...(council?.[documentsKey] || []),
      ],
    };

    await store.dispatch(updateCouncil(councilData));
  };

  const updateCouncilAnnouncementDocument = async (
    uploadedDocument: Document
  ) => {
    const councilData = {
      _id: council?._id,
      announcementDocument: uploadedDocument._id,
    };

    await store.dispatch(updateCouncil(councilData));
  };

  const handleAddDocument = async () => {
    try {
      setIsLoading(true);

      const cantAddDocument =
        !documentFile && !council?._id && !council?.society;
      if (cantAddDocument) {
        return;
      }

      const documentData = createDocumentData();

      const uploadedDocument = await store.dispatch(addDocument(documentData));

      if (uploadedDocument) {
        await updateSocietyWithDocument({
          societyId: council.society,
          uploadedDocument,
        });

        if (replaceAnnouncementDocument && council?.announcementDocument) {
          await updateDocumentToDeleted(council.announcementDocument);
          await updateCouncilAnnouncementDocument(uploadedDocument);
        } else {
          await updateCouncilWithDocument(uploadedDocument);
        }
      }

      fetchCouncilData();

      successSwal(t('DocumentUploadedSuccessfully'));
    } catch (error) {
      errorSwal(t('Error'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <>
      <AddCouncilDocumentModalHeader
        handleCloseModal={handleCloseModal}
        replaceAnnouncementDocument={replaceAnnouncementDocument}
      />

      <AddCouncilDocumentModalBody
        documentName={documentName}
        documentFile={documentFile}
        isPrivateDocument={isPrivateDocument}
        replaceAnnouncementDocument={replaceAnnouncementDocument}
        handleChangeDocumentName={handleChangeDocumentName}
        handleChangeDocumentFile={handleChangeDocumentFile}
        handleChangeIsPrivateDocument={handleChangeIsPrivateDocument}
      />

      <AddCouncilDocumentModalFooter
        documentName={documentName}
        documentFile={documentFile}
        handleAddDocument={handleAddDocument}
      />

      {isLoading && <CustomLoading />}
    </>
  );
};

export default AddCouncilDocumentModal;
